import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VideoModal from '../components/VideoModal';
import LicenseTierSelector from '../components/LicenseTierSelector';
import { useCart } from '../context/CartContext';
import { setSeoMetadata, generateProductSchema } from '../lib/seo';
import { toast } from '../hooks/use-toast';
import { getDocsBySlug } from '../data/docsContent';
import {
  ExternalLink, ShoppingCart, CheckCircle, XCircle, ShieldCheck, RefreshCw,
  Bell, LifeBuoy, Download, Package, Code2, Server, Cpu, Star, ArrowRight,
  ChevronLeft, ChevronRight, Loader2, Sparkles, Play, History, X, Tag,
  Zap, Wrench, Lock, AlertTriangle, BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_DEMO = 'https://demo.aijoomla.org';
const SECURITY_DEMO_VIDEO = 'https://customer-assets.emergentagent.com/job_960a7272-fe11-4b2f-a6b4-7f0bd404139b/artifacts/csr2700j_AI%20Joomla%20Security%20Suite-final.mp4';

const CL_STYLE = {
  feature:     { bg: 'bg-blue-500/15',   border: 'border-blue-500/30',   text: 'text-blue-300',   icon: Zap },
  improvement: { bg: 'bg-purple-500/15', border: 'border-purple-500/30', text: 'text-purple-300', icon: Sparkles },
  fix:         { bg: 'bg-green-500/15',  border: 'border-green-500/30',  text: 'text-green-300',  icon: Wrench },
  security:    { bg: 'bg-red-500/15',    border: 'border-red-500/30',    text: 'text-red-300',    icon: Lock },
  breaking:    { bg: 'bg-amber-500/15',  border: 'border-amber-500/30',  text: 'text-amber-300',  icon: AlertTriangle },
};

// Strip HTML tags + decode common HTML entities from a string.
const stripHtml = (s) =>
  (s || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#x[0-9a-fA-F]+;/g, '')
    .replace(/&#[0-9]+;/g, '')
    .trim();

// ── Changelog Slide-Out Panel ──────────────────────────────────────────────
function ChangelogPanel({ element, name, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/builder/public/changelog/${element}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ changelog: [] }))
      .finally(() => setLoading(false));
  }, [element]);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
        data-testid="changelog-backdrop"
      />
      {/* Slide-in panel */}
      <div
        className="fixed right-0 top-0 h-full w-full max-w-lg bg-[#0a1628] border-l border-white/10 z-50 flex flex-col shadow-2xl"
        data-testid="changelog-panel"
        style={{ animation: 'slideInRight 0.28s ease-out' }}
      >
        <style>{`@keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <History className="w-5 h-5 text-blue-400" />
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">Changelog</h2>
              <p className="text-gray-400 text-xs">{name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
            data-testid="changelog-close"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        {/* Current version pill */}
        {data?.current_version && (
          <div className="px-6 pt-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/15 text-blue-300 border border-blue-500/30">
              <Tag className="w-3.5 h-3.5" /> Current: v{data.current_version}
            </span>
          </div>
        )}
        {/* Entries */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : (data?.changelog || []).length === 0 ? (
            <div className="text-center py-20">
              <History className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No changelog entries yet.</p>
            </div>
          ) : (data.changelog.map((entry, i) => {
            const style = CL_STYLE[entry.type] || CL_STYLE.improvement;
            const Icon = style.icon;
            return (
              <div
                key={i}
                data-testid={`public-cl-entry-${i}`}
                className={`rounded-xl border p-4 ${style.bg} ${style.border}`}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold border ${style.bg} ${style.border} ${style.text}`}>
                    <Icon className="w-3 h-3" /> v{entry.version}
                  </span>
                  <span className={`text-xs font-medium capitalize ${style.text}`}>{entry.type}</span>
                  {entry.date && (
                    <span className="ml-auto text-xs text-gray-500">{entry.date}</span>
                  )}
                </div>
                {entry.notes && (
                  <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">{entry.notes}</p>
                )}
              </div>
            );
          }))}
        </div>
        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10">
          <p className="text-xs text-gray-500 text-center">
            {(data?.changelog || []).length} release{(data?.changelog || []).length !== 1 ? 's' : ''} documented
          </p>
        </div>
      </div>
    </>
  );
}

const ExtensionInfoPage = () => {
  const params = useParams();
  const seoUrl = params['*'];
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  // Is this a Security Suite product? Show Watch Demo modal instead of external link.
  const isSecurityProduct = (seoUrl || '').includes('ai-joomla-security');
  const docsSlug = (seoUrl || product?.seo_url || '').split('/').pop();
  const productDocs = docsSlug ? getDocsBySlug(docsSlug) : null;
  const hasDocs = productDocs && !productDocs.comingSoon;

  // Set default selected tier when product loads
  useEffect(() => {
    if (product?.license_options?.length) {
      const visible = product.license_options.filter(o => o.visible !== false);
      if (visible.length) setSelectedTier(visible[0]);
    }
  }, [product]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/store/products/seo/${seoUrl}`);
        setProduct(res.data);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [seoUrl]);

  useEffect(() => {
    if (product) {
      setSeoMetadata({
        title: `${product.name} — Joomla Extension | AI Joomla`,
        description: stripHtml(product.description).substring(0, 160) || `${product.name} — a premium Joomla extension for Joomla 4, 5 & 6.`,
        keywords: [product.category, product.name, 'joomla extension', 'joomla', 'AI Joomla'].filter(Boolean).join(', '),
        canonicalPath: `/extensions/${product.seo_url || seoUrl}`,
        ogType: 'product',
        ogImage: product.images?.[0] || product.image,
        jsonLd: generateProductSchema(product),
      });
    }
  }, [product, seoUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bots-dark flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-bots-dark">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-40 text-center">
          <Package className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-3">Extension not found</h1>
          <p className="text-gray-400 mb-6">We couldn't find that extension.</p>
          <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-500">
            Browse all extensions <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = (product.images && product.images.length ? product.images : [product.image]).filter(Boolean);
  const tier = product.extension_tier || (product.is_free_extension ? 'free' : 'pro');
  const isFree = tier === 'free';
  const demoUrl = product.extension_demo_url || DEFAULT_DEMO;
  const buyHref = product.seo_url ? `/shop/${product.seo_url}` : `/product/${product.id}`;

  // Joomla compatibility (default 4/5/6)
  const joomlaVersions = (() => {
    const t = product.extension_joomla_target || '';
    const f = [];
    if (!t) return ['4', '5', '6'];
    if (/4/.test(t)) f.push('4');
    if (/5/.test(t)) f.push('5');
    if (/6/.test(t)) f.push('6');
    return f.length ? f : ['4', '5', '6'];
  })();

  const singlePrice = product.single_site_price;
  const multiPrice = product.multi_site_price;
  const fromPrice = isFree ? 0 : (singlePrice ?? multiPrice ?? product.price);
  const hasProTier = Boolean(singlePrice || multiPrice);

  const comparison = [
    { label: 'Price', free: 'Free', pro: `From $${Number(singlePrice ?? multiPrice ?? product.price ?? 0).toFixed(2)}` },
    { label: 'Update notifications', free: true, pro: true, icon: Bell },
    { label: 'Priority support', free: false, pro: true, icon: LifeBuoy },
    { label: 'Automatic updates', free: false, pro: true, icon: RefreshCw },
    { label: 'Manual download & install', free: true, pro: true, icon: Download },
    { label: 'License key', free: true, pro: true, icon: ShieldCheck },
    { label: 'Registered sites', free: 'Unlimited', pro: 'Single or Multi-site' },
  ];

  const trustPoints = [
    { icon: ShieldCheck, title: 'Secure, licensed downloads', text: 'Every download is gated by a valid license key and delivered over encrypted storage.' },
    { icon: RefreshCw, title: 'Built for Joomla 4, 5 & 6', text: 'Maintained against the latest Joomla releases so your site stays current and safe.' },
    { icon: Code2, title: 'Clean, standards-based code', text: 'Developed in-house to Joomla coding standards — no bloat, no surprises.' },
    { icon: LifeBuoy, title: 'Real human support', text: 'Pro licenses include direct support from the developers who built the extension.' },
  ];

  const renderCell = (val) => {
    if (val === true) return <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />;
    if (val === false) return <XCircle className="w-5 h-5 text-slate-500 mx-auto" />;
    return <span className="text-sm text-slate-200">{val}</span>;
  };

  return (
    <div className="min-h-screen bg-bots-dark" data-testid="extension-info-page">
      <Header />
      <VideoModal
        isOpen={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoUrl={SECURITY_DEMO_VIDEO}
        title="AI Joomla Security Suite — Full Demo"
      />
      {changelogOpen && product?.extension_element && (
        <ChangelogPanel
          element={product.extension_element}
          name={product.name}
          onClose={() => setChangelogOpen(false)}
        />
      )}

      {/* Hero */}
      <section className="relative overflow-hidden pt-28 pb-14 border-b border-white/10">
        <div className="absolute -top-40 -left-40 w-[36rem] h-[36rem] bg-blue-600/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[36rem] h-[36rem] bg-purple-600/15 rounded-full blur-3xl" />
        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <Link to="/shop" className="hover:text-white">Extensions</Link>
            <ChevronRight className="w-4 h-4" />
            {product.category && (
              <>
                <Link to={`/shop/products?category=${(product.category || '').toLowerCase()}`} className="hover:text-white">{product.category}</Link>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
            <span className="text-gray-300">{product.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-10 items-start">
            {/* Gallery */}
            <div data-testid="info-gallery">
              <div className="rounded-2xl overflow-hidden bg-bots-surface border border-white/10 aspect-[6/5] flex items-center justify-center">
                {images.length ? (
                  <img src={images[activeImage]} alt={product.name} className="w-full h-full object-contain" />
                ) : (
                  <Package className="w-24 h-24 text-slate-700" />
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                  {images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`w-20 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-all ${
                        i === activeImage ? 'border-blue-400' : 'border-white/10 hover:border-white/30'
                      }`}
                      data-testid={`info-thumb-${i}`}
                    >
                      <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Summary */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${isFree ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'}`} data-testid="info-tier-badge">
                  {isFree ? 'FREE' : 'PRO'}
                </span>
                {product.extension_type && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-300 border border-white/10 capitalize">
                    <Package className="w-3.5 h-3.5" /> {product.extension_type}
                  </span>
                )}
                {product.extension_version && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-300 border border-white/10">v{product.extension_version}</span>
                )}
                {product.extension_element && (
                  <button
                    onClick={() => setChangelogOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-300 border border-blue-500/20 hover:bg-blue-500/20 transition-colors cursor-pointer"
                    data-testid="open-changelog-btn"
                  >
                    <History className="w-3.5 h-3.5" /> Changelog
                  </button>
                )}
              </div>

              <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">{product.name}</h1>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                {(stripHtml(product.short_description) || stripHtml(product.description).substring(0, 200) || 'A premium Joomla extension built for performance and reliability.')}
              </p>

              {/* Joomla compat */}
              <div className="flex items-center gap-2 mb-8" data-testid="info-joomla-badges">
                <span className="text-xs text-gray-400 uppercase tracking-wide mr-1">Compatible:</span>
                {joomlaVersions.map((v) => (
                  <span key={v} className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 text-blue-300 border border-blue-500/20 px-2.5 py-1 text-sm font-semibold">
                    <CheckCircle className="w-3.5 h-3.5" /> Joomla {v}
                  </span>
                ))}
              </div>

              {/* License Tier Selector + CTA */}
              <div className="bg-bots-surface border border-white/10 rounded-2xl p-6">
                {/* Tier cards */}
                {product.license_options?.filter(o => o.visible !== false).length > 0 ? (
                  <LicenseTierSelector
                    options={product.license_options}
                    selected={selectedTier}
                    onChange={setSelectedTier}
                  />
                ) : (
                  <div className="flex items-end gap-2 mb-5">
                    {isFree ? (
                      <span className="text-3xl font-extrabold text-white">Free</span>
                    ) : (
                      <>
                        <span className="text-sm text-gray-400 mb-1">From</span>
                        <span className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">${Number(fromPrice || 0).toFixed(2)}</span>
                        <span className="text-sm text-gray-400 mb-1">/ yr</span>
                      </>
                    )}
                  </div>
                )}

                {/* Add to cart / Get Free CTA */}
                <div className="flex flex-col sm:flex-row gap-3 mt-2">
                  <button
                    onClick={() => {
                      if (selectedTier) {
                        const isFreeOpt = selectedTier.license_type === 'free' || selectedTier.price === 0;
                        addToCart({
                          id: product.id,
                          name: product.name,
                          price: selectedTier.price ?? 0,
                          image: product.image || (product.images?.[0] ?? ''),
                          quantity: 1,
                          license_option: selectedTier,
                          license_option_id: selectedTier.id,
                          is_extension: true,
                          extension_element: product.extension_element,
                          extension_tier: selectedTier.license_type,
                          cart_key: `ext:${product.id}:${selectedTier.id}`,
                        });
                        toast({
                          title: isFreeOpt ? 'Added to cart — Free' : `Added to cart — ${selectedTier.label}`,
                          description: isFreeOpt
                            ? 'Proceed to checkout to get your free license key.'
                            : `$${selectedTier.price}/yr · Complete checkout to receive your license key.`,
                        });
                        return;
                      }
                      // No license_options on this product — flat single-price extension.
                      const price = isFree ? 0 : Number(fromPrice || 0);
                      addToCart({
                        id: product.id,
                        name: product.name,
                        price,
                        image: product.image || (product.images?.[0] ?? ''),
                        quantity: 1,
                        is_extension: true,
                        extension_element: product.extension_element,
                        extension_tier: tier,
                        cart_key: `ext:${product.id}`,
                      });
                      toast({
                        title: isFree ? 'Added to cart — Free' : 'Added to cart',
                        description: isFree
                          ? 'Proceed to checkout to get your free license key.'
                          : `$${price.toFixed(2)} · Complete checkout to receive your license key.`,
                      });
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all"
                    data-testid="info-add-to-cart"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {selectedTier
                      ? (selectedTier.price === 0 || selectedTier.license_type === 'free'
                          ? 'Get it Free'
                          : `Add to Cart — $${selectedTier.price}`)
                      : (isFree ? 'Get it Free' : `Add to Cart — $${Number(fromPrice || 0).toFixed(2)}`)}
                  </button>
                  {isSecurityProduct ? (
                    <button
                      onClick={() => setVideoOpen(true)}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
                      data-testid="info-watch-demo"
                    >
                      <Play className="w-5 h-5" fill="currentColor" /> Watch Demo
                    </button>
                  ) : (
                    <a
                      href={demoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
                      data-testid="info-live-demo"
                    >
                      <ExternalLink className="w-5 h-5" /> Live Demo
                    </a>
                  )}
                  {hasDocs && (
                    <Link
                      to={`/docs/${productDocs.slug}`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
                      data-testid="info-view-docs"
                    >
                      <BookOpen className="w-5 h-5" /> Documentation
                    </Link>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  {selectedTier?.price === 0 || selectedTier?.license_type === 'free'
                    ? 'Includes update notifications & a license key. Manual download & install.'
                    : 'Includes 12 months of automatic updates & priority support.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full description */}
      <section className="py-16 bg-bots-dark">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">About this extension</h2>
          <div className="prose prose-invert max-w-none text-gray-300 leading-relaxed" data-testid="info-description"
            dangerouslySetInnerHTML={{ __html: product.description || '<p>A premium Joomla extension built for performance and reliability. Compatible with Joomla 4, 5 &amp; 6, with instant download and — on Pro — automatic updates.</p>' }}
          />
        </div>
      </section>

      {/* FREE vs PRO comparison */}
      <section className="py-16 bg-bots-surface border-y border-white/10" data-testid="info-free-vs-pro">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">{hasProTier ? 'Free vs Pro' : "What's included"}</h2>
            <p className="text-gray-400">Everything you need to choose the right license for your site.</p>
          </div>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full">
              <thead>
                <tr className="bg-bots-dark/60">
                  <th className="text-left p-4 text-gray-300 font-semibold">Feature</th>
                  <th className="p-4 text-amber-300 font-bold text-center">Free</th>
                  {hasProTier && <th className="p-4 text-purple-300 font-bold text-center">Pro</th>}
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, idx) => (
                  <tr key={row.label} className={idx % 2 ? 'bg-white/[0.02]' : ''}>
                    <td className="p-4 text-gray-300 text-sm flex items-center gap-2">
                      {row.icon && <row.icon className="w-4 h-4 text-gray-500" />}
                      {row.label}
                    </td>
                    <td className="p-4 text-center">{renderCell(row.free)}</td>
                    {hasProTier && <td className="p-4 text-center">{renderCell(row.pro)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-center mt-8">
            <button
              onClick={() => navigate(buyHref)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-500 text-white font-bold rounded-full hover:from-purple-500 hover:to-indigo-400 transition-all"
              data-testid="info-compare-cta"
            >
              Choose your license <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Technical details */}
      <section className="py-16 bg-bots-dark">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">Technical details</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Package, label: 'Type', value: product.extension_type || 'Extension' },
              { icon: Star, label: 'Latest version', value: product.extension_version || '—' },
              { icon: Server, label: 'Joomla', value: joomlaVersions.map((v) => v).join(', ') },
              { icon: Cpu, label: 'Min PHP', value: product.extension_php_min || '8.1' },
            ].map((d) => (
              <div key={d.label} className="bg-bots-surface border border-white/10 rounded-xl p-5">
                <d.icon className="w-6 h-6 text-blue-400 mb-3" />
                <p className="text-xs text-gray-400 uppercase tracking-wide">{d.label}</p>
                <p className="text-white font-semibold mt-1">{d.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-16 bg-bots-surface border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm font-semibold mb-4">
              <Sparkles className="w-4 h-4" /> Why AI Joomla
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">Trusted Joomla development</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustPoints.map((t) => (
              <div key={t.title} className="text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-blue-500/15 flex items-center justify-center">
                  <t.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="text-white font-bold mb-2">{t.title}</h3>
                <p className="text-gray-400 text-sm">{t.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-bots-surface to-bots-dark">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to add {product.name}?</h2>
          <p className="text-gray-300 text-lg mb-8">Try the live demo, then grab your license — instant delivery to your account.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => {
                if (selectedTier) {
                  addToCart({ id: product.id, name: product.name, price: selectedTier.price ?? 0, image: product.image || (product.images?.[0] ?? ''), quantity: 1, license_option: selectedTier, license_option_id: selectedTier.id, is_extension: true, extension_element: product.extension_element, extension_tier: selectedTier.license_type, cart_key: `ext:${product.id}:${selectedTier.id}` });
                } else { navigate(buyHref); }
              }} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-bold rounded-full hover:from-blue-500 hover:to-cyan-400 transition-all" data-testid="info-final-buy">
              <ShoppingCart className="w-5 h-5" /> {selectedTier?.price === 0 || selectedTier?.license_type === 'free' ? 'Get it Free' : `Get ${selectedTier?.label ?? 'License'}`}
            </button>
            {isSecurityProduct ? (
              <button
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all"
                data-testid="info-final-watch-demo"
              >
                <Play className="w-5 h-5" fill="currentColor" /> Watch Demo
              </button>
            ) : (
              <a href={demoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 border border-white/15 text-white font-bold rounded-full hover:bg-white/10 transition-all">
                <ExternalLink className="w-5 h-5" /> Live Demo
              </a>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ExtensionInfoPage;
